import React, { useEffect, useRef, useState } from "react";
import {
    FormField, Button, BusinessPortalHeader, LottieLoadingIndicator,
    Toast, ImageCropModal, PortalFormActions, PortalFormScrollBody, PortalFormSection,
    PortalFormDescription, PortalFormLabel, PortalFormSectionDescription,
    PortalFormSectionHeader, PortalFormSectionTitle,
    PortalFormSurface, PortalSelectedIcon,
    PortalSelectedItem, PortalSelectedItemContent, PortalFormLinkButton,
    PortalSelect, StandardModalActionButton, StandardModalActionGroup, PortalFormTextArea, portalColors
} from "src/components";
import { Grid } from "@mui/material";
import styled from "styled-components";
import AddIcon from "src/img/new/add.svg";
import { PERK_TYPE, PROMO_CODE_TYPE, USER_GROUP, API_ERROR_FIELD } from "src/constants/perks";
import Dropzone from "react-dropzone";
import UsersModal from "./components/UsersModal";
import UsersIcon from "src/img/new/users.svg";
import { Text } from "src/components";
import ValidatedDateInput from "src/components/ValidatedDateInput";
import pluralize from "pluralize";
import { capitalizeString, customNames } from "src/utils/helpers";
import { ASPECT_RATIO_LABEL } from "src/scenes/Noticeboard/constants";

const InputLabel = PortalFormLabel;
const InputDescription = PortalFormDescription;

const PerkFieldStack = styled.div`
    display: grid;
    grid-template-columns: minmax(0, 1fr);
    gap: 1rem;
    width: 100%;
    min-width: 0;
`;

const PerkDetailsStack = styled.div`
    display: grid;
    grid-template-columns: minmax(0, 1fr);
    gap: 1rem;
    width: 100%;
    min-width: 0;

    > .MuiGrid-item {
        width: 100%;
        max-width: 100%;
        flex-basis: auto;
        padding: 0;
        min-width: 0;
    }
`;

const PerkDateStack = styled.div`
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 1rem;
    width: 100%;
    min-width: 0;

    @media (max-width: 640px) {
        grid-template-columns: minmax(0, 1fr);
    }
`;

const PerkRadioOptions = styled.div`
    display: flex;
    align-items: flex-start;
    justify-content: flex-start;
    gap: 1rem;
    flex-wrap: wrap;
`;

const PerkRadioButton = styled.button`
    position: relative;
    width: min(100%, ${p => p.width || "17.5rem"});
    min-height: 3rem;
    padding: 0.75rem 2.75rem 0.75rem 1.25rem;
    border: 1px solid ${p => p.active ? portalColors.text : portalColors.inputBorder};
    border-radius: var(--calendar-radius-control, 8px);
    text-align: center;
    font-family: Manrope, Helvetica, sans-serif;
    font-size: 0.875rem;
    font-weight: 700;
    color: ${p => p.active ? "var(--calendar-bg, #FFFFFF)" : portalColors.text};
    background-color: ${p => p.active ? portalColors.text : portalColors.surfaceSoft};
    box-shadow: ${portalColors.inputShadow};
    cursor: ${p => p.disabled ? "not-allowed" : "pointer"};
    opacity: ${p => p.disabled ? 0.62 : 1};
    white-space: nowrap;
    transition: background 0.16s ease, border-color 0.16s ease, color 0.16s ease;
`;

const PerkRadioCircle = styled.span`
    position: absolute;
    right: 0.75rem;
    top: 50%;
    width: 1.25rem;
    height: 1.25rem;
    border-radius: 50%;
    background-color: #76C00D;
    display: ${p => p.active ? "inline-flex" : "none"};
    justify-content: center;
    align-items: center;
    transform: translateY(-50%);
    color: #fff;
    font-size: 0.625rem;
    font-weight: 700;
`;

const TextInput = styled(FormField)`
    font-family: Manrope, Helvetica, sans-serif;
    height: 46px;
    border: 1px solid ${portalColors.borderStrong};
    border-radius: 8px;
    text-align: left;
    font-weight: 400;
    font-size: 0.938rem;
    color: ${portalColors.text};
    margin: 0;

    ::placeholder {
        text-align: left;
        color: ${portalColors.textSoft};
        font-family: Manrope, Helvetica, sans-serif;
        font-size: 0.938rem;
    }
`;

const TextArea = styled(PortalFormTextArea)`
    resize: none;
    height: 8.5rem;
`;

const ImageContainer = styled(Dropzone)`
    width: min(100%, 380px);
    height: 13.25rem;
    border: 1px solid ${portalColors.border};
    border-radius: 16px;
    background: ${portalColors.surfaceSoft};
    margin: ${p => p.margin || "0"};
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-direction: column;
    overflow: hidden;
`;

const UploadInfo = styled.div`
    padding: 0 1rem;
    box-sizing: border-box;
`;

const UploadButtonRow = styled.div`
    margin-top: 1rem;
    text-align: center;
`;

const ImagePreview = styled.img`
    width: 100%;
    height: 100%;
    object-fit: contain;
`;

const ErrorMessage = styled.div`
    font-size: 11.5px;
    color: #ff7171;
    font-weight: 500;
    line-height: 12.5px;
    margin: 4px 0 0 4px;

    &:empty {
        display: none;
    }
`;

const PerkUploadInfo = ({ buttonLabel = "Browse File", helpSize = "0.938rem", onBrowse, disabled = false }) => (
    <>
        <UploadInfo>
            <InputDescription color={portalColors.textSoft} size={helpSize} margin="0" align="left">
                Supported formats: JPG, PNG. Max size: 5MB. <br />
                Aspect Ratio 16:9 - 1920px x 1080px
            </InputDescription>
        </UploadInfo>
        <UploadButtonRow>
            <Button
                type="button"
                backgroundColor={portalColors.text}
                color="#fff"
                border={`1px solid ${portalColors.text}`}
                size="0.813rem"
                style={{ borderRadius: "8px", boxShadow: "none" }}
                onClick={onBrowse}
                disabled={disabled}
            >
                {buttonLabel}
            </Button>
        </UploadButtonRow>
    </>
);

const FormPage = ({
    user,
    handleFormVisible,
    handleChangePerkType,
    formData,
    handleInputChange,
    handleDateChange,
    handleCreateOrUpdateWithImages,
    handlePromoCodeChange,
    promoCodeType,
    updatingUuid,
    handleChangeUserGroup,
    isFormSaving,
    formErrors,
    handleGiftPerkRecipients
}) => {
    const [perkImage, setPerkImage] = useState(null);
    const [promoCodeImage, setPromoCodeImage] = useState(null);
    const perkImageDropzoneRef = useRef();
    const promoCodeImageDropzoneRef = useRef();
    const [showUsersModal, setShowUsersModal] = useState(false);
    const [editGiftPerkUserType, setEditGiftPerkUserType] = useState("");

    // Crop Image
    const [imageToCrop, setImageToCrop] = useState(null);
    const [croppedBlob, setCroppedBlob] = useState(null);
    const [showCropperModal, setShowCropperModal] = useState(false);
        
    const handleCropComplete = (file) => {
        if (!file) return;
        const url = URL.createObjectURL(file);
        if (perkImage) {
            URL.revokeObjectURL(perkImage);
        }

        setPerkImage(url); // For preview
        setShowCropperModal(false);
        setCroppedBlob(file);
    };

    useEffect(() => {
        if (promoCodeType === PROMO_CODE_TYPE.TEXT) {
            setPromoCodeImage(null);
        }
    }, [promoCodeType]);

    const isGeneral = (type) => type === PERK_TYPE.GENERAL;

    const isGiftPerk = (type) => type === PERK_TYPE.GIFT;

    const perkIsForEmployees = () => formData.allEmployeesInPerk && !formData.allClientsInPerk;

    const perkIsForClients = () => !formData.allEmployeesInPerk && formData.allClientsInPerk;

    const perkIsForEmployeesAndClients = () => formData.allEmployeesInPerk && formData.allClientsInPerk;

    const getErrorMessage = input => {
        const error = formErrors.find(error => error.input === input);
        return error ? `* ${error.message}` : null;
    };

    const handleDrop = (acceptedFiles, imageType) => {
        if (!acceptedFiles || acceptedFiles.length === 0) return;

        const file = acceptedFiles[0];

        const validImageTypes = ["image/jpeg", "image/png"];
        if (!validImageTypes.includes(file.type)) {
            Toast.error("Unsupported file type. Images may be JPEG or PNG format.");
            return;
        }

        const reader = new FileReader();
        reader.onload = () => {
            const result = reader.result;
            if (imageType === "perkImage") {
                setPerkImage(result);
                setImageToCrop(reader.result);
                setShowCropperModal(true);
            } else if (imageType === "promoCodeImage") {
                setPromoCodeImage(result);
            } else {
                console.warn("Unknown image type:", imageType);
            }
        };
        reader.readAsDataURL(file);
    };

    const handleShowUsersModal = () => {
        setShowUsersModal(true);
    };

    const handleCloseUsersModal = () => {
        setShowUsersModal(false);
    };

    const handleEditGiftPerkUsers = userType => {
        setEditGiftPerkUserType(userType);
        setShowUsersModal(true);
    };

    const handleBrowsePerkImage = () => {
        perkImageDropzoneRef.current?.open?.();
    };

    const handleBrowsePromoCodeImage = () => {
        promoCodeImageDropzoneRef.current?.open?.();
    };

    const giftPerkSelectedEmployeesCount = formData.selectedCompanyRecipients.filter(user => !user.isClient).length;

    const giftPerkSelectedClientsCount = formData.selectedCompanyRecipients.filter(user => user.isClient).length;

    return (
        <>
            <BusinessPortalHeader
                title={updatingUuid ? "Edit Perk" : "Create New Perk"}
                description="Design and assign exclusive perks for all employees and clients, or target specific groups. You can also create gift perks for individual users. Customize your perks to reward and engage your team with unique benefits."
                maxWidth="84.688rem"
            />

            <PortalFormSurface maxWidth="84.688rem" marginTop="0" flex="1 1 auto" minHeight="0">
                <PortalFormScrollBody
                    columns="repeat(2, minmax(0, 1fr))"
                    padding="1.25rem"
                    gap="1rem"
                    alignItems="start"
                >
                    <PortalFormSection>
                        <PortalFormSectionHeader withDivider>
                            <PortalFormSectionTitle>
                                Perk Information
                            </PortalFormSectionTitle>
                            <PortalFormSectionDescription>
                                Define the perk type, audience, validity dates and public details.
                            </PortalFormSectionDescription>
                        </PortalFormSectionHeader>

                        <PerkDetailsStack>
                            <Grid item style={{ marginBottom: "13px" }}>
                                <InputLabel>
                                    Select Perk Type
                                </InputLabel>
                                <InputDescription>
                                    Select wether this perk will be available to most users or gifted to specific users.
                                </InputDescription>
                                <PerkRadioOptions>
                                    <PerkRadioButton
                                        type="button"
                                        active={isGeneral(formData.type)}
                                        onClick={() => handleChangePerkType(PERK_TYPE.GENERAL)}
                                        disabled={isFormSaving}
                                    >
                                        General Perk
                                        <PerkRadioCircle active={isGeneral(formData.type)}>&#x2713;</PerkRadioCircle>
                                    </PerkRadioButton>
                                    <PerkRadioButton
                                        type="button"
                                        active={isGiftPerk(formData.type)}
                                        onClick={() => handleChangePerkType(PERK_TYPE.GIFT)}
                                        disabled={isFormSaving}
                                    >
                                        Gift Perk
                                        <PerkRadioCircle active={isGiftPerk(formData.type)}>&#x2713;</PerkRadioCircle>
                                    </PerkRadioButton>
                                </PerkRadioOptions>
                            </Grid>

                            {formData.type === PERK_TYPE.GENERAL && (
                                <Grid item md={10}>
                                    <InputLabel>
                                        Select Perk User Group
                                    </InputLabel>
                                    <InputDescription>
                                        Select to display this perk to employees, clients or both user groups.
                                    </InputDescription>
                                    <PerkRadioOptions>
                                        <PerkRadioButton
                                            type="button"
                                            active={perkIsForEmployees()}
                                            onClick={() => handleChangeUserGroup(USER_GROUP.EMPLOYEES)}
                                            disabled={isFormSaving}
                                        >
                                            All {capitalizeString(pluralize(customNames.employees))}
                                            <PerkRadioCircle active={perkIsForEmployees()}>&#x2713;</PerkRadioCircle>
                                        </PerkRadioButton>
                                        <PerkRadioButton
                                            type="button"
                                            active={perkIsForClients()}
                                            onClick={() => handleChangeUserGroup(USER_GROUP.CLIENTS)}
                                            disabled={isFormSaving}
                                        >
                                            All {capitalizeString(pluralize(customNames.clients))}
                                            <PerkRadioCircle active={perkIsForClients()}>&#x2713;</PerkRadioCircle>
                                        </PerkRadioButton>
                                        <PerkRadioButton
                                            type="button"
                                            active={perkIsForEmployeesAndClients()}
                                            onClick={() => handleChangeUserGroup(USER_GROUP.BOTH)}
                                            disabled={isFormSaving}
                                        >
                                            Both
                                            <PerkRadioCircle active={perkIsForEmployeesAndClients()}>&#x2713;</PerkRadioCircle>
                                        </PerkRadioButton>
                                    </PerkRadioOptions>
                                    <ErrorMessage>{ getErrorMessage(API_ERROR_FIELD.RECIPIENTS) ?? "" }</ErrorMessage>
                                </Grid>
                            )}

                            {formData.type === PERK_TYPE.GIFT && (
                                <Grid item md={9}>
                                    <InputLabel>
                                        Select Gift Perk Users
                                    </InputLabel>
                                    <InputDescription>
                                        Select the users you with to gift this perk to.
                                    </InputDescription>

                                    {!formData.selectedCompanyRecipients.length && (
                                        <Button
                                            disabled={isFormSaving}
                                            onClick={handleShowUsersModal}
                                            backgroundColor="#000"
                                            color="#fff"
                                            width="14.5rem"
                                            height="2.688rem"
                                            padding="0"
                                            style={{
                                                borderRadius: "12px",
                                                marginBottom: "7.5px",
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                position: "relative"
                                            }}
                                        >
                                            <p style={{ marginRight: "1.688rem" }}>Add People</p>
                                            <img src={AddIcon} style={{ height: "2.25rem", position: "absolute", right: "59px", top: "6px" }} />
                                        </Button>
                                    )}

                                    {formData.selectedCompanyRecipients.length > 0 && (
                                        <Grid container direction="row" justifyContent="flex-start" alignItems="center" spacing={1}>
                                            <Grid item md={6}>
                                                <PortalSelectedItem>
                                                    <PortalSelectedItemContent>
                                                        <PortalSelectedIcon>
                                                            <img src={UsersIcon} style={{ height: "16px", width: "16px" }} />
                                                        </PortalSelectedIcon>

                                                        <Text
                                                            size="0.875rem"
                                                            weight="500"
                                                            color="#000"
                                                        >
                                                            { formData.allEmployeesInPerk ? "All" : giftPerkSelectedEmployeesCount } { formData.allEmployeesInPerk || giftPerkSelectedEmployeesCount > 1 ? `${capitalizeString(pluralize(customNames.employees))}` : `${capitalizeString(pluralize.singular(customNames.employees))}` }
                                                        </Text>
                                                    </PortalSelectedItemContent>

                                                    <PortalFormLinkButton
                                                        type="button"
                                                        onClick={() => handleEditGiftPerkUsers(USER_GROUP.EMPLOYEES)}
                                                    >
                                                        Edit
                                                    </PortalFormLinkButton>
                                                </PortalSelectedItem>
                                            </Grid>

                                            <Grid item md={6}>
                                                <PortalSelectedItem>
                                                    <PortalSelectedItemContent>
                                                        <PortalSelectedIcon>
                                                            <img src={UsersIcon} style={{ height: "16px", width: "16px" }} />
                                                        </PortalSelectedIcon>

                                                        <Text
                                                            size="0.875rem"
                                                            weight="500"
                                                            color="#000"
                                                        >
                                                            { formData.allClientsInPerk ? "All" : giftPerkSelectedClientsCount } { formData.allClientsInPerk || giftPerkSelectedClientsCount > 1 ? `${capitalizeString(pluralize(customNames.clients))}` : `${capitalizeString(pluralize.singular(customNames.clients))}` }
                                                        </Text>
                                                    </PortalSelectedItemContent>

                                                    <PortalFormLinkButton
                                                        type="button"
                                                        onClick={() => handleEditGiftPerkUsers(USER_GROUP.CLIENTS)}
                                                    >
                                                        Edit
                                                    </PortalFormLinkButton>
                                                </PortalSelectedItem>
                                            </Grid>
                                        </Grid>
                                    )}
                                    <ErrorMessage>{ getErrorMessage(API_ERROR_FIELD.RECIPIENTS) ?? "" }</ErrorMessage>
                                </Grid>
                            )}

                            <Grid item xs={12}>
                                <InputLabel>
                                    Select Perk Validation Dates
                                </InputLabel>
                                <InputDescription>
                                    Choose the start and end dates to define how long this perk will be available.
                                </InputDescription>
                                <PerkDateStack>
                                    <div style={{ minWidth: 0 }}>
                                        <ValidatedDateInput
                                            placeholder="Select start date"
                                            onChange={momentObj => handleDateChange("startDate", momentObj)}
                                            errors={[]}
                                            value={formData.startDate}
                                            disabled={false}
                                            showErrors={false}
                                            size={"0.938rem"}
                                        />
                                    </div>
                                    <div style={{ minWidth: 0 }}>
                                        <ValidatedDateInput
                                            placeholder="Select end date"
                                            onChange={momentObj => handleDateChange("endDate", momentObj)}
                                            errors={[]}
                                            value={formData.endDate}
                                            disabled={false}
                                            showErrors={false}
                                            size={"0.938rem"}
                                        />
                                    </div>
                                </PerkDateStack>
                                <ErrorMessage>{ getErrorMessage(API_ERROR_FIELD.DATES) ?? "" }</ErrorMessage>
                            </Grid>

                            <Grid item xs={12}>
                                <PerkFieldStack>
                                    <div>
                                        <InputLabel>
                                            Company Name Of Perk
                                        </InputLabel>
                                        <InputDescription>
                                            Enter the name of the retailer or provider offering this perk.
                                        </InputDescription>
                                        <TextInput
                                            name="companyName"
                                            value={formData.companyName}
                                            placeholder="e.g., “Ray-Ban”"
                                            onChange={handleInputChange}
                                            disabled={isFormSaving}
                                        />
                                        <ErrorMessage>{ getErrorMessage(API_ERROR_FIELD.COMPANY) ?? "" }</ErrorMessage>
                                    </div>

                                    <div>
                                        <InputLabel>
                                            Perk Heading
                                        </InputLabel>
                                        <InputDescription>
                                            Enter a clear and engaging title for your perk.
                                        </InputDescription>
                                        <TextInput
                                            name="title"
                                            value={formData.title}
                                            placeholder="e.g., “20% Off Your Next Purchase”"
                                            onChange={handleInputChange}
                                            disabled={isFormSaving}
                                        />
                                        <ErrorMessage>{ getErrorMessage(API_ERROR_FIELD.HEADING) ?? "" }</ErrorMessage>
                                    </div>

                                    <div>
                                        <InputLabel>
                                            Perk Description
                                        </InputLabel>
                                        <InputDescription>
                                            Provide a brief description of the perk, outlining the benefits and details for users.
                                        </InputDescription>
                                        <TextArea
                                            name="description"
                                            value={formData.description}
                                            placeholder="e.g., “Enjoy exclusive savings on all products this month”"
                                            onChange={handleInputChange}
                                            disabled={isFormSaving}
                                            background={portalColors.surface}
                                        />
                                        <ErrorMessage>{ getErrorMessage(API_ERROR_FIELD.DESCRIPTION) ?? "" }</ErrorMessage>
                                    </div>

                                    <div>
                                        <InputLabel>
                                            Perk Company Website
                                        </InputLabel>
                                        <InputDescription>
                                            Enter the website URL for the company offering the perk.
                                        </InputDescription>
                                        <TextInput
                                            name="website"
                                            value={formData.website}
                                            placeholder="e.g., www.companyname.com"
                                            onChange={handleInputChange}
                                            disabled={isFormSaving}
                                        />
                                        <ErrorMessage>{ getErrorMessage(API_ERROR_FIELD.WEBSITE) ?? "" }</ErrorMessage>
                                    </div>
                                </PerkFieldStack>
                            </Grid>
                        </PerkDetailsStack>
                    </PortalFormSection>

                    <PortalFormSection>
                        <PortalFormSectionHeader withDivider>
                            <PortalFormSectionTitle>
                                Assets & Code
                            </PortalFormSectionTitle>
                            <PortalFormSectionDescription>
                                Upload perk artwork and choose how users redeem the offer.
                            </PortalFormSectionDescription>
                        </PortalFormSectionHeader>
                        <Grid container spacing={3}>
                            <Grid item md={12}>
                                <PortalFormSectionTitle>
                                    Upload Perk Image
                                </PortalFormSectionTitle>
                                <InputDescription size="0.938rem" fontWeight="400">
                                    Upload the company’s logo or an image representing the perk.
                                </InputDescription>
                                <ImageContainer
                                    ref={perkImageDropzoneRef}
                                    onDrop={(file) => handleDrop(file, "perkImage")}
                                >
                                    { (perkImage || formData.imageUrl) && (
                                        <ImagePreview src={perkImage || formData.imageUrl} />
                                    )}
                                    { (!perkImage && !formData.imageUrl) && (
                                        <PerkUploadInfo disabled={isFormSaving} />
                                    )}
                                </ImageContainer>
                                { (perkImage || formData.imageUrl) && <PerkUploadInfo buttonLabel="Change Image" onBrowse={handleBrowsePerkImage} disabled={isFormSaving} /> }
                                <ErrorMessage>{ getErrorMessage(API_ERROR_FIELD.IMAGE) ?? "" }</ErrorMessage>
                            </Grid>

                            <Grid item md={12}>
                                <PortalFormSectionTitle>
                                    Perk Code
                                </PortalFormSectionTitle>

                                <InputDescription fontWeight="500">
                                    Select to provide a promo/discount code, or upload an image asset 
                                    such as a QR code or barcode for this perk.
                                </InputDescription>
                                <PortalSelect
                                    name="promoCodeType"
                                    onChange={handlePromoCodeChange}
                                    disabled={isFormSaving}
                                    value={promoCodeType}
                                    width="100%"
                                    minWidth="0"
                                    height="46px"
                                    background={portalColors.surface}
                                >
                                    <option value={PROMO_CODE_TYPE.TEXT}>Promo/Discount Code</option>
                                    <option value={PROMO_CODE_TYPE.IMAGE}>Image (eg: QR code/Barcode)</option>
                                </PortalSelect>

                                {promoCodeType === PROMO_CODE_TYPE.TEXT && (
                                    <>
                                        <InputDescription margin="13px 0 10px">
                                            Enter you perk promo/discount code here.
                                        </InputDescription>
                                        <TextInput
                                            name="promoCode"
                                            value={formData.promoCode}
                                            placeholder="e.g., WELCOME10"
                                            onChange={handleInputChange}
                                            disabled={isFormSaving}
                                        />
                                    </>
                                )}

                                {promoCodeType === PROMO_CODE_TYPE.IMAGE && (
                                    <>
                                        <ImageContainer
                                            margin="35px 0 0"
                                            ref={promoCodeImageDropzoneRef}
                                            onDrop={(file) => handleDrop(file, "promoCodeImage")}
                                        >
                                            { (promoCodeImage || formData.promoCodeImageUrl) && (
                                                <ImagePreview src={promoCodeImage || formData.promoCodeImageUrl} />
                                            )}
                                            { (!promoCodeImage && !formData.promoCodeImageUrl) && (
                                                <PerkUploadInfo helpSize="0.813rem" disabled={isFormSaving} />
                                            )}
                                        </ImageContainer>
                                        { (promoCodeImage || formData.promoCodeImageUrl) && <PerkUploadInfo buttonLabel="Change Image" helpSize="0.813rem" onBrowse={handleBrowsePromoCodeImage} disabled={isFormSaving} /> }
                                    </>
                                )}
                                <ErrorMessage>{ getErrorMessage(API_ERROR_FIELD.CODE_TYPE) ?? "" }</ErrorMessage>
                            </Grid>
                        </Grid>
                    </PortalFormSection>
                </PortalFormScrollBody>

                <PortalFormActions sticky={false}>
                    <StandardModalActionGroup $pushRight>
                        <StandardModalActionButton type="button" onClick={handleFormVisible} disabled={isFormSaving}>
                            Cancel
                        </StandardModalActionButton>
                        <StandardModalActionButton
                            type="button"
                            $variant="primary"
                            onClick={() => handleCreateOrUpdateWithImages(croppedBlob, promoCodeImage)}
                            disabled={isFormSaving}
                        >
                            {isFormSaving
                                ? <LottieLoadingIndicator height="1.5rem" width="1.5rem" containerHeight="1.5rem" margin="0" />
                                : "Save"}
                        </StandardModalActionButton>
                    </StandardModalActionGroup>
                </PortalFormActions>
            </PortalFormSurface>
            <ImageCropModal
                imageSrc={imageToCrop}
                isOpen={showCropperModal}
                onClose={(shouldClear) => {
                    setShowCropperModal(false);
                    if (shouldClear) {
                        setImageToCrop(null); 
                        setPerkImage(null);
                    }
                }}
                onCropComplete={handleCropComplete}
                aspectRatios={[
                    { width: 16, height: 9, label: "16:9", standardName: ASPECT_RATIO_LABEL.WIDESCREEN },
                ]}
                allowedOrientations={["landscape"]}
                lockedAspectRatio={undefined}
            />

            {showUsersModal && (
                <UsersModal
                    user={user}
                    showUsersModal={showUsersModal}
                    handleCloseUsersModal={handleCloseUsersModal}
                    handleGiftPerkRecipients={handleGiftPerkRecipients}
                    userType={editGiftPerkUserType}
                    selectedRecipients={formData.selectedCompanyRecipients}
                />
            )}
        </>
    );
};

export default FormPage;
